import { fillText } from 'components/invoiceImport/invoiceImportText';
import {
  INVOICE_ERROR_TEXTS,
  titleMonth,
} from 'components/invoiceImportJournal/invoiceJournalText';
import { normalizePennylaneCategoryId } from 'helper/companyOverviewDiff';
import { paymentTermLabel } from 'constants/paymentTerms';

/**
 * What the "Historique" tab of an invoice's detail says of each event
 * (JBWEB-1278). The api gives codes, ids and statuses only (api README,
 * decision 44): every word is written here. `texts` carries three readers,
 * one per locale space: `text` (`invoiceHistory`), `journalText`
 * (`invoiceJournal`: the error labels and steps of the import journal) and
 * `listText` (`invoiceList`: the payment status labels of the list).
 */

/** The label of each type the api writes or reserves (api INVOICE_EVENT_TYPES). */
export const INVOICE_EVENT_LABELS = {
  fetched: 'Récupérée depuis Bestt',
  imported: 'Importée dans Pennylane',
  import_failed: 'Erreur d’import',
  pennylane_category_set: 'Catégorie posée dans Pennylane',
  category_changed: 'Catégorie modifiée',
  payment_term_changed: 'Délai de paiement modifié',
  payment_status_changed: 'Statut de paiement modifié',
  appendix_added: 'Annexe ajoutée',
  appendix_sent: 'Annexe envoyée dans Pennylane',
  appendix_deleted: 'Annexe supprimée',
  // Written by cards 1282 to 1284; the litigation ones say it as the tab does.
  sent_by_mail: 'Facture envoyée par mail',
  reminder_sent: 'Relance envoyée',
  litigation_opened: 'Passage en contentieux',
  litigation_closed: 'Sortie du contentieux',
  // An admin stopped or restarted the automatic reminders (JBWEB-1283).
  reminders_suspended: 'Relances automatiques suspendues',
  reminders_resumed: 'Relances automatiques reprises',
};

export const UNKNOWN_EVENT_LABEL = 'Autre évènement';

// The payment statuses of api utils/invoiceStatus.js, worded as the list does.
const PAYMENT_STATUS_LABELS = {
  draft: { key: 'draft', fallback: 'Brouillon' },
  pending: { key: 'pending', fallback: 'En attente' },
  partially_paid: { key: 'partiallyPaid', fallback: 'Partiellement payé' },
  paid: { key: 'paid', fallback: 'Payé' },
  cancelled: { key: 'cancelled', fallback: 'Annulée' },
};

const DETAIL_FALLBACKS = {
  linked: 'Rattachée : Pennylane avait déjà cette facture',
  sent: 'Envoyée dans Pennylane par Jobbiz',
  noCategory: 'Aucune catégorie',
  unknownCategory: 'Catégorie n° {id}',
  termDays: '{days} jours',
  newDueDate: 'Nouvelle échéance le {date}',
  sameDueDate: 'Échéance inchangée',
  'categoryPennylane.updated': 'Mise à jour dans Pennylane',
  'categoryPennylane.on_import': 'Sera posée dans Pennylane à l’import',
  'categoryPennylane.skipped': 'Pennylane non modifié hors production',
  'termPennylane.updated': 'Échéance mise à jour dans Pennylane',
  'termPennylane.on_import': 'Échéance envoyée à Pennylane à l’import',
  'termPennylane.skipped': 'Pennylane non modifié hors production',
  'appendix.sent': 'Envoyée dans Pennylane',
  'appendix.pending': 'En attente d’import',
  'appendix.skipped': 'Non envoyée dans Pennylane hors production',
  remainsInPennylane: 'Elle reste dans Pennylane',
  confirmedByAdmin: 'Confirmée par un admin',
  'reminderLevel.before_due': 'Rappel',
  'reminderLevel.first': 'Relance 1/3',
  'reminderLevel.second': 'Relance 2/3',
  'reminderLevel.third': 'Relance 3/3',
  'reminderTrigger.manual': 'manuelle',
  'reminderTrigger.auto': 'automatique',
  recipientsOne: '{count} destinataire',
  recipientsMany: '{count} destinataires',
  redirected: 'Envoyée à l’adresse de test : aucun client ne l’a reçue',
};

const isOwnKey = (table, key) =>
  Object.prototype.hasOwnProperty.call(table, key);

const detail = (text, key, values) => {
  const value = text(`details.${key}`, DETAIL_FALLBACKS[key]);
  return values ? fillText(value, values) : value;
};

const transition = (text, from, to) =>
  fillText(text('transition', '{from} → {to}'), { from, to });

const DASH = '—';

const statusLabel = (listText, status) => {
  if (status == null) return DASH;
  if (!isOwnKey(PAYMENT_STATUS_LABELS, status)) return String(status);
  const { key, fallback } = PAYMENT_STATUS_LABELS[status];
  return listText(`status.${key}`, fallback);
};

/** The name of a Pennylane category; `categories` is null while it is read. */
const categoryName = (text, id, categories) => {
  if (id == null) return detail(text, 'noCategory');
  if (categories === null) return '…';
  const found = (categories ?? []).find(
    (category) =>
      normalizePennylaneCategoryId(category.id) ===
      normalizePennylaneCategoryId(id),
  );
  return found?.label ?? detail(text, 'unknownCategory', { id });
};

const termLabel = (text, days, endOfMonth, language) => {
  if (!Number.isInteger(days)) return DASH;
  return (
    paymentTermLabel({ days, endOfMonth: Boolean(endOfMonth) }, language) ??
    detail(text, 'termDays', { days })
  );
};

/** "15/08/2026" for "2026-08-15", without going through a time zone. */
export const formatDueDay = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : null;
};

const pennylaneOutcome = (text, space, outcome) =>
  isOwnKey(DETAIL_FALLBACKS, `${space}.${outcome}`)
    ? detail(text, `${space}.${outcome}`)
    : null;

const errorLabel = (journalText, code) =>
  isOwnKey(INVOICE_ERROR_TEXTS, code)
    ? journalText(`errors.${code}.label`, INVOICE_ERROR_TEXTS[code].label)
    : code;

const STEP_LABELS = {
  bestt: { key: 'stepBestt', fallback: 'Étape 1 · Bestt' },
  pennylane: { key: 'stepPennylane', fallback: 'Étape 2 · Pennylane' },
};

// The lines under the label, each a short sentence; the order is the one read.
const DETAIL_BUILDERS = {
  imported: ({ text }, details) => {
    if (details.linked === true) return [detail(text, 'linked')];
    if (details.linked === false) return [detail(text, 'sent')];
    return [];
  },
  import_failed: ({ journalText }, details) => {
    const step = isOwnKey(STEP_LABELS, details.step)
      ? journalText(
          STEP_LABELS[details.step].key,
          STEP_LABELS[details.step].fallback,
        )
      : null;
    const code = details.code
      ? `${errorLabel(journalText, details.code)} · ${details.code}`
      : null;
    return [[step, code].filter(Boolean).join(' · ')].filter(Boolean);
  },
  pennylane_category_set: ({ text }, details, { categories }) => [
    categoryName(text, details.categoryId, categories),
  ],
  category_changed: ({ text }, details, { categories }) => [
    transition(
      text,
      categoryName(text, details.from, categories),
      categoryName(text, details.to, categories),
    ),
    pennylaneOutcome(text, 'categoryPennylane', details.pennylane),
  ],
  payment_term_changed: ({ text }, details, { language }) => {
    const lines = [
      transition(
        text,
        termLabel(text, details.fromDays, details.fromEndOfMonth, language),
        termLabel(text, details.toDays, details.toEndOfMonth, language),
      ),
    ];
    const dueDate = formatDueDay(details.toDueDate);
    if (dueDate && details.toDueDate !== details.fromDueDate) {
      lines.push(detail(text, 'newDueDate', { date: dueDate }));
    } else if (dueDate) {
      lines.push(detail(text, 'sameDueDate'));
    }
    lines.push(pennylaneOutcome(text, 'termPennylane', details.pennylane));
    return lines;
  },
  payment_status_changed: ({ text, listText }, details) => [
    transition(
      text,
      statusLabel(listText, details.from),
      statusLabel(listText, details.to),
    ),
  ],
  appendix_added: ({ text }, details) => [
    pennylaneOutcome(text, 'appendix', details.pennylane) ??
      pennylaneOutcome(text, 'appendix', details.status),
  ],
  // An admin found in Pennylane an appendix the import sent without an
  // answer (JBWEB-1277, decision 57): Jobbiz did not see it arrive.
  appendix_sent: ({ text }, details) =>
    details.confirmedBy === 'admin' ? [detail(text, 'confirmedByAdmin')] : [],
  appendix_deleted: ({ text }, details) =>
    details.remainsInPennylane === true
      ? [detail(text, 'remainsInPennylane')]
      : [],
  // Its step, manual or automatic, and how many received it (JBWEB-1283); the
  // api keeps no address nor name in the event. A mail diverted to the test
  // address reached no client: said, so that no one counts it as sent.
  reminder_sent: ({ text }, details) => {
    const step = isOwnKey(DETAIL_FALLBACKS, `reminderLevel.${details.level}`)
      ? detail(text, `reminderLevel.${details.level}`)
      : null;
    const trigger = isOwnKey(
      DETAIL_FALLBACKS,
      `reminderTrigger.${details.trigger}`,
    )
      ? detail(text, `reminderTrigger.${details.trigger}`)
      : null;
    const count = details.recipientCount;
    const recipients =
      Number.isInteger(count) && count >= 0
        ? detail(text, count === 1 ? 'recipientsOne' : 'recipientsMany', {
            count,
          })
        : null;
    return [
      [step, trigger, recipients].filter(Boolean).join(' · '),
      details.outcome === 'redirected' ? detail(text, 'redirected') : null,
    ];
  },
};

/**
 * The label and the detail lines of an event. A type the screen does not know
 * gets a generic label and no detail: its fields are not worded.
 */
export const historyEventTexts = (texts, event, context = {}) => {
  const { type } = event;
  if (!isOwnKey(INVOICE_EVENT_LABELS, type)) {
    return {
      label: texts.text('types.unknown', UNKNOWN_EVENT_LABEL),
      details: [],
    };
  }
  const builder = DETAIL_BUILDERS[type];
  const details = builder
    ? builder(texts, event.details ?? {}, context).filter(Boolean)
    : [];
  return {
    label: texts.text(`types.${type}`, INVOICE_EVENT_LABELS[type]),
    details,
  };
};

const fullName = (author) =>
  [author?.firstname, author?.lastname]
    .filter((part) => typeof part === 'string' && part.trim())
    .join(' ');

/**
 * Who is behind an event: "par Léa Martin", "réessai · par Léa Martin" for a
 * retry, "automatique" when neither a person nor a run is. An author that no
 * longer exists says nothing, or "réessai" alone.
 */
export const authorMention = (text, event) => {
  if (event.automatic) return text('automatic', 'automatique');
  const name = fullName(event.author);
  const isRetry = event.runType === 'retry';
  if (!name) return isRetry ? text('retry', 'réessai') : null;
  return fillText(
    isRetry
      ? text('retryBy', 'réessai · par {name}')
      : text('by', 'par {name}'),
    { name },
  );
};

const toDate = (value) => {
  const date = new Date(value);
  return value == null || Number.isNaN(date.getTime()) ? null : date;
};

/** "03/10/2026 à 14:32", in the browser's time, as the import journal. */
export const formatEventAt = (text, at) => {
  const date = toDate(at);
  if (!date) return null;
  return fillText(text('at', '{date} à {time}'), {
    date: date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
    time: date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  });
};

const monthKeyOf = (date) =>
  date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    : 'unknown';

/**
 * The events by month, newest first ("Octobre 2026"). The api sorts them
 * already; a stable sort keeps its order for events of the same instant.
 */
export const groupEventsByMonth = (text, events) => {
  const sorted = [...events].sort((a, b) => {
    const left = toDate(a.at)?.getTime() ?? -Infinity;
    const right = toDate(b.at)?.getTime() ?? -Infinity;
    return right - left;
  });
  const groups = [];
  sorted.forEach((event) => {
    const date = toDate(event.at);
    const key = monthKeyOf(date);
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      group = {
        key,
        label: date
          ? titleMonth(
              date.toLocaleDateString('fr-FR', {
                month: 'long',
                year: 'numeric',
              }),
            )
          : text('unknownDate', 'Date inconnue'),
        events: [],
      };
      groups.push(group);
    }
    group.events.push(event);
  });
  return groups;
};
